import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { connect } from 'react-redux';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import mapValues from 'lodash/mapValues';

import { shadeGridFlagSelector } from '../../userData/store/selectors';

import Button from '../button.component';
import SectionHeader from './sectionHeader.component';
import ColumnHeaders from './columnHeaders.component';
import BodyRow from './bodyRow.component';

import {
  DATA,
  SECTION_HEADER,
  COLUMN_HEADER,
  PARENT_DATA,
  PARENT_SUMMARY,
  PARENT_SUMMARY_DOUBLE,
  CHILD_DATA,
  CHILD_DATA_BOLD,

  DATA_BORDER_TOPLINE,
  DATA_BORDER_DOUBLEBOTTOMLINE,
  DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
  TEXT,
} from './rowTypes';

const closePopups = debounce(() => ReactTooltip.hide(), 200);

function applyRowStateOverride(rowsState, rowId, updateData) {
  return ({
    ...rowsState,
    [rowId]: {
      ...rowsState[rowId] ?? { rowId },
      ...updateData,
    },
  });
}
const Report = ({
  closePopupsOnScroll,
  data,
  isRowShadingEnabled,
  useCustomDecimalNumber,
  useBlankZeros,
  allowRowShadowing,
  suppressHidingDecimalPlacesForPercentage,
  sectionHeaderPrefix,
  useHistorical,
  disableDrillLink,
  showExpandCollapseAllButton,
}) => {
  const [shouldAutoExpandChildren, setShouldAutoExpandChildren] = useState(false);
  const [rowsState, setRowsState] = useState({});

  const toggleRow = useCallback(rowId => {
    const isExpanded = rowsState => !!rowsState[rowId]?.expanded;
    setRowsState(state => applyRowStateOverride(state, rowId, { expanded: !isExpanded(state) }));
  }, []);

  const expandAll = useCallback(() => {
    setShouldAutoExpandChildren(true);
    setRowsState(state => mapValues(state, rowsState => ({
      ...rowsState,
      expanded: true,
    })));
  }, []);

  const collapseAll = useCallback(() => {
    setShouldAutoExpandChildren(false);
    setRowsState(state => mapValues(state, rowState => ({
      ...rowState,
      expanded: false,
    })));
  }, []);

  const closePopup = useCallback(() => {
    if (closePopupsOnScroll) {
      closePopups();
    }
  }, [closePopupsOnScroll]);

  const columnNumbers = useMemo(() => [...Array(data.columnCount).keys()], [data.columnCount]);
  const columnHeaders = useMemo(() =>
    data.rows.find(({ rowType }) => rowType === COLUMN_HEADER)?.columns?.map?.(({ value }) => value) ?? [],
  [data.rows]
  );
  const reportWithPreText = useMemo(() => Boolean(data.rows.find(row => row.preText)), [data.rows]);

  const expandCollapseButtons = useMemo(() => showExpandCollapseAllButton && (
    <div className="d-flex justify-content-end">
      <Button onClick={expandAll}>
        Expand all rows
      </Button>
      <Button kind="secondary" className="ml-2" onClick={collapseAll}>
        Collapse all rows
      </Button>
    </div>
  ), [showExpandCollapseAllButton, expandAll, collapseAll]);

  if (!data) {
    return <span>No data to display</span>;
  }

  const { stickyHeaders, stickyDescriptions } = data;
  let lastNonDataRow = 0;

  return (
    <div className="report">
      { expandCollapseButtons }
      <div
        className={classnames('report-body', {
          'report-body-shaded': allowRowShadowing && isRowShadingEnabled,
          'report-body-sticky-headers': stickyHeaders,
          'report-body-sticky-descriptions': stickyDescriptions,
        })}
        onScroll={closePopup}
      >
        {data.rows.map((row, index) => {
          switch (row.rowType) {
            case SECTION_HEADER: {
              lastNonDataRow = index;
              return (
                <SectionHeader key={index} text={`${sectionHeaderPrefix || ''}${row.description}`} />
              );
            }
            case COLUMN_HEADER: {
              lastNonDataRow = index;
              return (
                <ColumnHeaders
                  reportWithPreText={reportWithPreText}
                  columnNumbers={columnNumbers}
                  key={index}
                  row={row}
                />
              );
            }
            case PARENT_DATA:
            case PARENT_SUMMARY:
            case PARENT_SUMMARY_DOUBLE: {
              lastNonDataRow = index;

              return (
                <BodyRow
                  reportWithPreText={reportWithPreText}
                  columnNumbers={columnNumbers}
                  columnHeaders={columnHeaders}
                  rowId={String(index)}
                  toggleRow={toggleRow}
                  shouldAutoExpandChildren={shouldAutoExpandChildren}
                  rowsState={rowsState}
                  key={index}
                  row={row}
                  useCustomDecimalNumber={useCustomDecimalNumber}
                  suppressHidingDecimalPlacesForPercentage={suppressHidingDecimalPlacesForPercentage}
                  useBlankZeros={useBlankZeros}
                  useHistorical={useHistorical}
                  disableDrillLink={disableDrillLink}
                />
              );
            }
            case DATA:
            case CHILD_DATA:
            case CHILD_DATA_BOLD:
            case DATA_BORDER_TOPLINE:
            case DATA_BORDER_DOUBLEBOTTOMLINE:
            case DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE:
            case TEXT:
              return (
                <BodyRow
                  reportWithPreText={reportWithPreText}
                  columnNumbers={columnNumbers}
                  columnHeaders={columnHeaders}
                  key={index}
                  rowId={String(index)}
                  toggleRow={toggleRow}
                  shouldAutoExpandChildren={shouldAutoExpandChildren}
                  rowsState={rowsState}
                  row={row}
                  isOddDataRow={!!((index - lastNonDataRow) % 2)}
                  useCustomDecimalNumber={useCustomDecimalNumber}
                  suppressHidingDecimalPlacesForPercentage={suppressHidingDecimalPlacesForPercentage}
                  useBlankZeros={useBlankZeros}
                  useHistorical={useHistorical}
                  disableDrillLink={disableDrillLink}
                />
              );
            default: {
              lastNonDataRow = index;
              return null;
            }
          }
        })}
      </div>
    </div>
  );
};

Report.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    orientation: PropTypes.string.isRequired,
    columnCount: PropTypes.number.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
      rowType: PropTypes.string.isRequired,
    })).isRequired,
    stickyHeaders: PropTypes.bool,
    stickyDescriptions: PropTypes.bool,
  }).isRequired,
  isRowShadingEnabled: PropTypes.bool.isRequired,
  useCustomDecimalNumber: PropTypes.bool,
  useBlankZeros: PropTypes.bool,
  allowRowShadowing: PropTypes.bool,
  suppressHidingDecimalPlacesForPercentage: PropTypes.bool,
  closePopupsOnScroll: PropTypes.bool,
  sectionHeaderPrefix: PropTypes.string,
  useHistorical: PropTypes.bool,
  disableDrillLink: PropTypes.bool,
  showExpandCollapseAllButton: PropTypes.bool,
};


export default connect(state => ({
  isRowShadingEnabled: shadeGridFlagSelector(state),
}))(Report);
